import { RefObject } from "react";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";

import { GenericObject } from "src/api/flowTypes";
import {
  ComputePositionFn,
  ComputePositionOptions,
  curryComputePosition,
} from "src/base-components/FloatingWindow/positioning";

const MIN_ORDER = 100;

export type FloatingWindowType =
  | "detailed-view"
  | "chart"
  | "copilot"
  | "json-schema-editor"
  | "flow-json-schema"
  | "cm-task"
  | "cm-action"
  | "manifest-node-builder";
export type FloatingWindowProps = {
  id: string;
  computePosition: ComputePositionFn;
  data: GenericObject;
};

type FloatingWindowOptions = {
  id: string;
  target: RefObject<HTMLDivElement> | undefined;
  positionOptions?: ComputePositionOptions;
  type: FloatingWindowType;
};

type FloatingWindow<D = GenericObject> = {
  id: string;
  computePosition: ComputePositionFn;
  type: FloatingWindowType;
  isPinned: boolean;
  data: D;
  order: number;
};

type FloatingWindowsState = {
  windows: Record<string, FloatingWindow>;
};

type FloatingWindowsActions = {
  open: (window: FloatingWindowOptions, data?: GenericObject) => void;
  close: (id: string) => void;
  setPin: (id: string, isPinned: boolean) => void;
  moveUp: (id: string) => void;
  updateData: (id: string, data: GenericObject) => void;
};

type FloatingWindowsStore = ZustandStore<
  FloatingWindowsState,
  FloatingWindowsActions
>;

export const createFloatingWindowsStore = () => {
  return createStore(
    immer<FloatingWindowsStore>((set) => ({
      windows: {},
      actions: {
        open: ({ target, positionOptions = {}, ...window }, data = {}) => {
          return set((state) => {
            state.windows[window.id] = {
              ...window,
              isPinned: false,
              computePosition: curryComputePosition(target, positionOptions),
              data,
              order: getHighestOrder(state) + 1,
            };
          });
        },
        close: (id) =>
          set((state) => {
            delete state.windows[id];
          }),
        setPin: (id, isPinned) =>
          set((state) => {
            if (state.windows[id]) {
              state.windows[id].isPinned = isPinned;
            }
          }),
        moveUp: (id) =>
          set((state) => {
            if (state.windows[id]) {
              const highestOrder = getHighestOrder(state);
              if (state.windows[id].order < highestOrder) {
                state.windows[id].order = highestOrder + 1;
              }
            }
          }),
        updateData: (id, data) =>
          set((state) => {
            if (state.windows[id]) {
              state.windows[id].data = data;
            }
          }),
      },
    })),
  );
};

const getHighestOrder = (state: FloatingWindowsState) => {
  return Math.max(
    MIN_ORDER,
    ...Object.values(state.windows).map((window) => window.order),
  );
};
